import { ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 py-5">
      <Link to="/" className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-blue-50 text-blue-700">
          <ShieldCheck size={24} />
        </div>

        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">
          RevUShield
        </h1>
      </Link>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="border-b border-slate-200 pb-6 mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>

          <p className="text-slate-500 mt-2">Last updated: May 2026</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <p>
            RevUShield (“RevUShield,” “we,” “our,” or “us”) respects your
            privacy. This Privacy Policy explains how we collect, use, store,
            and protect information when you use our website, applications, and
            services.
          </p>

          <p>
            By using RevUShield, creating an account, or connecting a Google
            Business Profile, you agree to the practices described in this
            Privacy Policy.
          </p>

          <Section title="1. Information We Collect">
            <p>We may collect the following types of information:</p>

            <h3>Account Information</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Name</li>
              <li>Email address</li>
              <li>Login and authentication information</li>
              <li>Business account details</li>
            </ul>

            <h3>Google Business Profile Data</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Business name, location, and profile details</li>
              <li>Google reviews, ratings, reviewer names, and review text</li>
              <li>Owner responses and response history</li>
              <li>Google account connection status</li>
            </ul>

            <h3>Billing Information</h3>
            <p>
              Payments may be processed by third-party payment providers such as
              Stripe. RevUShield does not store full credit card numbers.
            </p>

            <h3>Usage and Technical Data</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Pages visited and features used</li>
              <li>Device, browser, and general location information</li>
              <li>IP address</li>
              <li>Error logs and performance data</li>
            </ul>
          </Section>

          <Section title="2. How We Use Information">
            <p>We use collected information to:</p>

            <ul className="list-disc pl-5 space-y-2">
              <li>Provide and operate RevUShield</li>
              <li>Sync and display Google Business Profile reviews</li>
              <li>Generate AI-assisted review response suggestions</li>
              <li>Send approval emails, notifications, and service updates</li>
              <li>Process subscriptions and billing</li>
              <li>Improve platform functionality, security, and reliability</li>
              <li>Enforce our Terms of Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </Section>

          <Section title="3. Google API Data">
            <p>
              RevUShield uses Google APIs to access Google Business Profile data
              that you authorize during account connection.
            </p>

            <p>
              We only use Google data to provide features you request, such as
              syncing reviews, generating response suggestions, and assisting
              with review response workflows.
            </p>

            <p>
              We do not sell Google user data. We do not use Google user data
              for advertising. You may revoke RevUShield’s access at any time
              through your Google account permissions.
            </p>
          </Section>

          <Section title="4. AI Processing">
            <p>
              RevUShield may use artificial intelligence providers to generate
              suggested responses to customer reviews.
            </p>

            <p>
              Information sent for AI processing may include review text,
              business name, business category, response preferences, and
              relevant business context.
            </p>

            <p>
              AI-generated responses may be inaccurate, incomplete, or
              inappropriate. You are responsible for reviewing and approving any
              response before it is posted or sent.
            </p>
          </Section>

          <Section title="5. Third-Party Services">
            <p>
              We may use third-party providers to operate RevUShield, including:
            </p>

            <ul className="list-disc pl-5 space-y-2">
              <li>
                Google APIs for authentication and Business Profile access
              </li>
              <li>AI providers for response generation</li>
              <li>Stripe or similar providers for payment processing</li>
              <li>
                Email providers for transactional emails and notifications
              </li>
              <li>Hosting, database, analytics, and security providers</li>
            </ul>

            <p>
              These providers may process information only as needed to provide
              their services to RevUShield.
            </p>
          </Section>

          <Section title="6. Data Sharing">
            <p>
              We do not sell your personal information. We may share information
              only when necessary to:
            </p>

            <ul className="list-disc pl-5 space-y-2">
              <li>Provide the Service</li>
              <li>Process payments</li>
              <li>Comply with law or legal process</li>
              <li>Protect RevUShield, users, or the public</li>
              <li>Complete a business transfer, merger, or acquisition</li>
            </ul>
          </Section>

          <Section title="7. Data Security">
            <p>
              We use reasonable administrative, technical, and organizational
              safeguards to protect your information.
            </p>

            <p>
              However, no internet-based service can be guaranteed to be
              completely secure. You are responsible for maintaining the
              security of your account credentials.
            </p>
          </Section>

          <Section title="8. Data Retention">
            <p>
              We retain information for as long as necessary to provide the
              Service, comply with legal obligations, resolve disputes, enforce
              agreements, and maintain security.
            </p>

            <p>
              You may request deletion of your account or certain data by
              contacting us. Some information may remain temporarily in backups,
              logs, or records required by law.
            </p>
          </Section>

          <Section title="9. Your Choices and Rights">
            <p>You may:</p>

            <ul className="list-disc pl-5 space-y-2">
              <li>Update account information</li>
              <li>Disconnect your Google account</li>
              <li>Cancel your subscription</li>
              <li>Request access to your data</li>
              <li>Request correction or deletion of your data</li>
              <li>Opt out of non-essential communications</li>
            </ul>

            <p>
              Depending on your location, you may have additional rights under
              applicable privacy laws.
            </p>
          </Section>

          <Section title="10. Cookies and Tracking">
            <p>
              RevUShield may use cookies or similar technologies to support
              login sessions, security, preferences, analytics, and platform
              performance.
            </p>

            <p>
              You can manage cookies through your browser settings, but
              disabling certain cookies may affect how the Service works.
            </p>
          </Section>

          <Section title="11. Children’s Privacy">
            <p>
              RevUShield is not intended for individuals under 18 years old. We
              do not knowingly collect personal information from children.
            </p>
          </Section>

          <Section title="12. Changes to This Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time. If we make
              material changes, we may notify you by email, through the Service,
              or by updating the “Last updated” date.
            </p>

            <p>
              Continued use of RevUShield after changes become effective means
              you accept the updated Privacy Policy.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              If you have questions about this Privacy Policy or want to make a
              privacy request, contact:
            </p>

            <p>
              RevUShield
              <br />
              Email:{" "}
              <a href="mailto:support@revushield.com">support@revushield.com</a>
            </p>
          </Section>
        </div>
      </div>

      <footer className="mt-10 border-t border-slate-200 pt-5 pb-2 text-sm text-slate-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p>© 2026 RevUShield</p>

        <div className="flex flex-wrap items-center gap-4">
          <Link to="/legal/terms" className="hover:text-slate-900 transition">
            Terms
          </Link>

          <Link to="/legal/privacy" className="hover:text-slate-900 transition">
            Privacy
          </Link>

          <a
            href="mailto:support@revushield.com"
            className="hover:text-slate-900 transition"
          >
            Support
          </a>

          <span className="text-slate-400">v1.0</span>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold text-slate-900 mb-3">{title}</h2>

      <div className="space-y-3 text-slate-700">{children}</div>
    </section>
  );
}
